import { ChangeDetectionStrategy, Component, computed, effect, inject, OnInit, signal } from '@angular/core';
import { ButtonComponent, IActivarTab, TabsComponent } from '@webapp/ui';
import { ToastService } from '@webapp/ui/components/toast/toast.service';
import { AuthService } from '@webapp/auth/services/auth.service';
import type { CsrfMissingError } from '@webapp/auth/interceptors/xsrf.interceptor';
import { UserRole } from '../../../interfaces/users.interface';
import { MyProfileStore } from '../../../store/my-profile-store.service';
import { ProfileApiKeysComponent } from '../../components/profile-api-keys/profile-api-keys.component';
import { ProfileInfoComponent } from '../../components/profile-info/profile-info.component';
import { ProfileSecurityComponent } from '../../components/profile-security/profile-security.component';
import { ProfileSessionsComponent } from '../../components/profile-sessions/profile-sessions.component';

export const ProfileTabNames = {
	Info: 'Información',
	Sessions: 'Sesiones',
	Security: 'Seguridad',
	ApiKeys: 'API keys',
};

@Component({
	selector: 'app-my-profile-page',
	standalone: true,
	imports: [
		TabsComponent,
		ButtonComponent,
		ProfileInfoComponent,
		ProfileSessionsComponent,
		ProfileSecurityComponent,
		ProfileApiKeysComponent,
	],
	templateUrl: './my-profile-page.component.html',
	styleUrl: './my-profile-page.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MyProfilePageComponent implements OnInit {
	readonly store = inject(MyProfileStore);
	private readonly toastService = inject(ToastService);
	private readonly authService = inject(AuthService);

	profile = this.store.select('profile');
	profileLoading = this.store.select('profileLoading');
	sessions = this.store.select('sessions');
	sessionsLoading = this.store.select('sessionsLoading');
	sessionsError = this.store.select('sessionsError');
	canRevokeOthers = this.store.canRevokeOthers;

	tabNames = ProfileTabNames;
	isSuperadmin = computed(() => this.authService.user()?.role === UserRole.SUPERADMIN);
	tabs = computed<IActivarTab[]>(() => {
		const base: IActivarTab[] = [ProfileTabNames.Info, ProfileTabNames.Sessions, ProfileTabNames.Security];
		return this.isSuperadmin() ? [...base, ProfileTabNames.ApiKeys] : base;
	});
	currentTab = signal<string>(ProfileTabNames.Info);
	// Drives the highlighted tab so it follows currentTab (e.g. the role fallback to Info).
	activeTabIndex = computed(() => Math.max(0, this.tabs().indexOf(this.currentTab())));

	constructor() {
		// If the role stops being SUPERADMIN while the API keys tab is open, fall back to Info.
		effect(() => {
			if (!this.isSuperadmin() && this.currentTab() === ProfileTabNames.ApiKeys) {
				this.currentTab.set(ProfileTabNames.Info);
			}
		});
	}

	private sessionsLoaded = false;

	ngOnInit(): void {
		this.store.loadProfile();
	}

	setCurrentTab(index: number): void {
		this.currentTab.set(this.tabs()[index]);

		if (this.currentTab() === ProfileTabNames.Sessions && !this.sessionsLoaded) {
			void this.fetchSessions();
		}
	}

	retrySessions(): void {
		void this.fetchSessions();
	}

	private async fetchSessions(): Promise<void> {
		await this.store.loadSessions();
		// Only latch the "already loaded" guard on a genuine success — a failure must
		// remain retryable (via the Sessions tab's retry button) rather than permanently
		// blocking re-fetches.
		this.sessionsLoaded = !this.store.select('sessionsError')();
	}

	async revokeOne(sid: string): Promise<void> {
		try {
			await this.store.revokeSession(sid);
		} catch (error) {
			this.handleRevokeError(error);
		}
	}

	async revokeAll(): Promise<void> {
		try {
			await this.store.revokeOthers();
		} catch (error) {
			this.handleRevokeError(error);
		}
	}

	private handleRevokeError(error: unknown): void {
		// A CsrfMissingError never carries an HTTP status — it's a synthetic client-side
		// rejection thrown by xsrfInterceptor BEFORE the request leaves the browser
		// (missing XSRF-TOKEN cookie), so it must be checked before any status-based branch.
		if ((error as Partial<CsrfMissingError>)?.csrfMissing) {
			this.toastService.showToast(
				'Problema de seguridad',
				'No pudimos verificar tu sesión de forma segura. Actualizá la página e intentá de nuevo.',
				'error'
			);
			return;
		}

		const status = (error as { status?: number })?.status;

		if (status === 404) {
			this.toastService.showToast(
				'Sesión no encontrada',
				'Esa sesión ya había sido revocada. Actualizamos la lista.',
				'error'
			);
			this.store.loadSessions();
			return;
		}

		if (status === 403) {
			this.toastService.showToast('No autorizado', 'No tenés permiso para revocar esa sesión.', 'error');
			return;
		}

		this.toastService.showToast('Error', 'No se pudo completar la acción. Intentá nuevamente.', 'error');
	}
}
